import { useState, useEffect } from "react";
import { H3, Label } from "../Text";
import { toast } from "react-toastify";
import Circle from "../Animation/Circle";
import { Button } from "../Buttons";
import { Request } from "../../hooks/Request";
import TableComplete from "../Tables/TableComplete";
import { requestAuth } from "../services/RequestService";
import { useDialog } from "../../contexts/DialogContext";
import { DialogConfirmation } from "../Modal";
import { numberWithDecimals, validateArray } from "../../utils/validate";
import { FileManagerModal } from "./FileManagerModal";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";

const CONCEPT_OPTIONS = [
  { value: "Maritimo", label: "Marítimo" },
  { value: "Terrestre", label: "Terrestre" },
  { value: "Seguro", label: "Seguro" },
  { value: "Aduana", label: "Aduana" },
  { value: "Aéreo", label: "Aéreo" },
];

const InputSubDataPaymentsWithItems = ({ field }) => {
  const { openDialog, dialogClose } = useDialog();

  const { data, loading, call } = Request({
    urlApi: `/dataSet/procedure/${field.id}/field/${field.fieldId}/subdata`,
  });

  const {
    data: providers,
    loading: loadingProviders,
    call: callProviders,
  } = Request({
    urlApi: "/Dropdown/providers/options",
  });

  const {
    data: expenseConcepts,
    loading: loadingExpenseConcepts,
    call: callExpenseConcepts,
  } = Request({
    urlApi: "/Dropdown/list-expenses/options",
  });

  useEffect(() => {
    call();
    callProviders(); // Ensure providers are loaded on mount
    callExpenseConcepts(); // Ensure expense concepts are loaded on mount
  }, [field.id]);

  function handleForm(item) {
    const values = item.description2
      ? JSON.parse(item.description2) || null
      : null;
    openDialog(
      <div className="w-[800px] max-w-full">
        {(loadingProviders || loadingExpenseConcepts) && (
          <FormAddDocument
            item={
              item
                ? {
                    ...item,
                    provider: values?.provider || null,
                    concept: values?.concept || "",
                    expenseDetails: values?.expenseDetails || [],
                    totalAmount: item.numberValue || "",
                    tentativeDate: values?.tentativeDate || "", // Use tentativeDate for creation/edit
                    status: values?.status || "Planificado",
                    paymentDate: values?.paymentDate || "",
                  }
                : null
            }
            reload={call}
            field={field}
            providers={providers}
            expenseConcepts={expenseConcepts}
            callExpenseConcepts={callExpenseConcepts}
            onClose={dialogClose}
          />
        )}
        <br />
      </div>,
    );
  }

  function handleWorkflow(item) {
    const values = item.description2 ? JSON.parse(item.description2) : {};
    openDialog(
      <div className="w-[450px] max-w-full">
        <FormUpdateStatus
          item={item}
          currentStatus={values?.status || "Planificado"}
          accountingDeliveryDate={values?.accountingDeliveryDate || ""} // Pass current accounting delivery date
          values={values}
          reload={() => {
            call();
            dialogClose();
          }}
        />
      </div>,
    );
  }

  function handleDelete(data) {
    openDialog(
      <DialogConfirmation
        children={<H3>¿Estas seguro que quieres eliminar este item?</H3>}
        onClose={dialogClose}
        method="delete"
        url={`/dataSet/procedure/subData/${data.id}`}
        texBtn="Eliminar"
        handleSuccess={call}
      />,
    );
  }

  function handleFiles(item) {
    openDialog(
      <FileManagerModal item={item} reload={call} onClose={dialogClose} />,
    );
  }

  return (
    <div className="bg-gray-300/40 p-2 rounded-md">
      <div className="flex justify-between items-center">
        <Label htmlFor={field.name} className={`${field.className} my-0`}>
          {field.labelCustom ? field.labelCustom : field.label}
        </Label>
      </div>

      <div className="bg-gray-100 rounded-lg p-3 pt-2">
        {(loadingProviders || loadingExpenseConcepts) && (
          <FormAddDocument
            reload={call}
            field={field}
            providers={providers}
            expenseConcepts={expenseConcepts}
            callExpenseConcepts={callExpenseConcepts}
            onClose={dialogClose}
          />
        )}
      </div>
      <br />
      {!loading ? (
        <Circle />
      ) : (
        <div className="w-full">
          <TableComplete
            header={[
              {
                name: "index",
                label: "#",
              },
              {
                name: "provider",
                label: "Proveedor",
                type: "custom",
                builder: (_, __, item) => {
                  const values = item.description2
                    ? JSON.parse(item.description2)
                    : null;
                  return (
                    <h3 className="font-medium text-blue-700">
                      {values?.provider?.name || "-"}
                      {values?.provider?.codigo_tr4 && (
                        <span className="text-gray-500 text-xs block">
                          ({values.provider.codigo_tr4})
                        </span>
                      )}
                    </h3>
                  );
                },
              },
              {
                name: "concept",
                label: "Concepto",
                type: "custom",
                builder: (_, __, item) => {
                  const values = item.description2
                    ? JSON.parse(item.description2)
                    : null;
                  return (
                    <h3 className="font-normal">{values?.concept || "-"}</h3>
                  );
                },
              },
              {
                name: "expenseDetails",
                label: "Items",
                type: "custom",
                builder: (_, __, item) => {
                  const values = item.description2
                    ? JSON.parse(item.description2)
                    : null;
                  if (
                    !values?.expenseDetails ||
                    values.expenseDetails.length === 0
                  )
                    return "-";
                  return (
                    <div className="text-[9px] text-gray-500 leading-tight max-w-[150px]">
                      {values.expenseDetails.map((ex, i) => (
                        <div key={i} className="truncate">
                          • {ex.concept}:{" "}
                          <span className="font-medium text-gray-700">
                            {numberWithDecimals(ex.amount)}{" "}
                            {ex.currency || "Bs."}
                          </span>
                        </div>
                      ))}
                    </div>
                  );
                },
              },
              {
                name: "totalAmount",
                label: "Total",
                type: "custom",
                builder: (_, __, item) => {
                  const values = item.description2
                    ? JSON.parse(item.description2)
                    : {};
                  const isPaid = values.status === "Pagado";
                  const colorClass = isPaid
                    ? "text-green-600"
                    : "text-blue-700";

                  if (
                    values.expenseDetails &&
                    values.expenseDetails.length > 0
                  ) {
                    const totals = values.expenseDetails.reduce((acc, curr) => {
                      const currency = curr.currency || "Bs.";
                      acc[currency] =
                        (acc[currency] || 0) + (parseFloat(curr.amount) || 0);
                      return acc;
                    }, {});

                    return (
                      <div className="flex flex-col">
                        {Object.entries(totals).map(([currency, amount]) => (
                          <h3
                            key={currency}
                            className={`font-bold ${colorClass}`}
                          >
                            {numberWithDecimals(amount)}{" "}
                            <span className="text-xs">{currency}</span>
                          </h3>
                        ))}
                      </div>
                    );
                  }

                  return (
                    <h3 className={`font-bold ${colorClass}`}>
                      {numberWithDecimals(item.numberValue || 0)}
                    </h3>
                  );
                },
              },
              {
                name: "status",
                label: "Estado",
                type: "custom",
                builder: (_, __, item) => {
                  const values = item.description2
                    ? JSON.parse(item.description2)
                    : null;
                  const status = values?.status || "Planificado";
                  const colors = {
                    Planificado: "bg-gray-100 text-gray-600",
                    Solicitado: "bg-amber-100 text-amber-700",
                    Pagado: "bg-green-600 text-white shadow-sm",
                  };
                  return (
                    <span
                      className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${colors[status]}`}
                    >
                      {status}
                    </span>
                  );
                },
              },
              {
                name: "tentative",
                label: "Fecha Estimada de Pago",
                type: "custom",
                builder: (_, __, item) => {
                  const values = item.description2
                    ? JSON.parse(item.description2)
                    : null;
                  return (
                    <h3 className="font-normal">
                      {values?.tentativeDate || "-"}
                    </h3>
                  );
                },
              },
              /* {
                name: "description",
                label: "Descripción",
              }, */
              {
                name: "fContabilidad",
                label: "Fecha Contabilidad",
                type: "custom",
                builder: (_, __, item) => {
                  const values = item.description2
                    ? JSON.parse(item.description2)
                    : null;
                  return (
                    <h3 className="font-normal text-gray-600">
                      {values?.accountingDeliveryDate || "-"}
                    </h3>
                  );
                },
              },
              {
                name: "fPago",
                label: "Fecha de Pago",
                type: "custom",
                builder: (_, __, item) => {
                  const values = item.description2
                    ? JSON.parse(item.description2)
                    : null;
                  return (
                    <h3 className="font-normal">
                      {values?.paymentDate || "-"}
                    </h3>
                  );
                },
              },
              {
                name: "add",
                type: "action",
                label: "Opciones",
                sticky: true,
                actions: [
                  {
                    label: "Actualizar Estado",
                    icon: "fas fa-sync-alt",
                    action: handleWorkflow,
                    color: "text-amber-600",
                  },
                  {
                    label: "Documentos",
                    icon: "fas fa-paperclip",
                    action: handleFiles,
                    color: "text-purple-600",
                  },
                  {
                    label: "Editar",
                    icon: "fas fa-edit",
                    action: handleForm,
                    color: "text-[#1d4ed8]",
                  },
                  {
                    label: "Eliminar",
                    icon: "fas fa-trash",
                    action: handleDelete,
                    color: "text-red-500",
                  },
                ],
              },
            ]}
            data={data?.map((item, i) => ({ ...item, index: i + 1 }))}
          />
        </div>
      )}
    </div>
  );
};

const FormAddDocument = ({
  item = null,
  reload,
  field,
  onClose,
  providers,
  expenseConcepts,
  callExpenseConcepts,
}) => {
  const [values, setValues] = useState({
    provider: item?.provider || null, // { id, name, data: { codigo_tr4 } }
    concept: item?.concept || "", // Maritimo, Terrestre, etc.
    expenseDetails: item?.expenseDetails || [], // [{ concept, amount }]
    totalAmount: item?.totalAmount || 0, // Calculated
    tentativeDate: item?.tentativeDate || "", // Fecha Tentativa
    paymentDate: item?.paymentDate || "", // Fecha de pago efectuada (se gestiona en el workflow)
    status: item?.status || "Planificado",
    description: item?.description ? item.description : "",
  });

  useEffect(() => {
    const newTotal = values.expenseDetails.reduce(
      (sum, detail) => sum + parseFloat(detail.amount || 0),
      0,
    );
    setValues((prev) => ({ ...prev, totalAmount: newTotal }));
  }, [values.expenseDetails]);

  async function handleSubmit() {
    if (!values.provider) return toast.error("El proveedor es obligatorio");
    if (!values.concept) return toast.error("El concepto es obligatorio");
    if (values.expenseDetails.length === 0)
      return toast.error("Debe agregar al menos un detalle de gasto");

    const totalsByCurrency = values.expenseDetails.reduce((acc, curr) => {
      const currency = curr.currency || "Bs.";
      acc[currency] = (acc[currency] || 0) + (parseFloat(curr.amount) || 0);
      return acc;
    }, {});

    const desc2 = {
      provider: values.provider,
      concept: values.concept,
      expenseDetails: values.expenseDetails,
      tentativeDate: values.tentativeDate, // Store as tentativeDate
      status: values.status,
      paymentDate: values.paymentDate,
      ...Object.keys(totalsByCurrency).reduce((acc, curr) => {
        const suffix =
          curr.charAt(0).toUpperCase() +
          curr.slice(1).toLowerCase().replace(".", "");
        acc[`amount${suffix}`] = totalsByCurrency[curr];
        return acc;
      }, {}),
    };
    await requestAuth(
      item ? "put" : "post",
      item
        ? `/dataSet/procedure/subData/${item.id}`
        : `/dataSet/procedure/${field.id}/field/${field.fieldId}/subdata`,
      {
        Label: values.provider.name, // Display provider name in table
        Description: values.description,
        NumberValue: values.totalAmount, // Calculated total
        Description2: JSON.stringify(desc2),
      },
    )
      .then(() => {
        reload();
        onClose();
        setValues({
          provider: null,
          concept: "",
          expenseDetails: [],
          totalAmount: 0,
          tentativeDate: "",
          status: "Planificado",
          paymentDate: "",
          description: "",
        });
        toast.success("Registro exitoso");
      })
      .catch((err) => {
        console.error(err);
        toast.error("Se produjo un error al guardar");
      });
  }

  const handleProviderChange = (selectedOption) => {
    if (selectedOption) {
      const selectedProvider = providers.find(
        (p) => p.name === selectedOption.value,
      );
      setValues((prev) => ({
        ...prev,
        provider: {
          id: selectedProvider.id,
          name: selectedProvider.name,
          codigo_tr4: JSON.parse(selectedProvider.data).codigo_tr4,
        },
      }));
    } else {
      setValues((prev) => ({ ...prev, provider: null }));
    }
  };

  const handleConceptChange = (selectedOption) => {
    setValues((prev) => ({ ...prev, concept: selectedOption?.value || "" }));
  };

  const handleExpenseDetailChange = (index, fieldName, value) => {
    const newDetails = [...values.expenseDetails];
    newDetails[index][fieldName] = value;
    setValues((prev) => ({ ...prev, expenseDetails: newDetails }));
  };

  const handleAddExpenseDetail = () => {
    setValues((prev) => ({
      ...prev,
      expenseDetails: [
        ...prev.expenseDetails,
        { concept: "", amount: "", currency: "Bs." },
      ],
    }));
  };

  const handleRemoveExpenseDetail = (index) => {
    const newDetails = values.expenseDetails.filter((_, i) => i !== index);
    setValues((prev) => ({ ...prev, expenseDetails: newDetails }));
  };

  const handleCreateExpenseConcept = async (inputValue, index) => {
    const newOption = { Name: inputValue, DropdownListLabel: "list-expenses" };
    await requestAuth("post", "/Dropdown/Option", newOption)
      .then(() => {
        toast.success(`Concepto de gasto "${inputValue}" creado.`);
        handleExpenseDetailChange(index, "concept", inputValue); // Auto-selecciona el nuevo concepto
        callExpenseConcepts(); // Recargar la lista de conceptos
      })
      .catch(() => toast.error("Error al crear concepto de gasto."));
  };

  return (
    <div className="flex flex-col gap-4 p-4 bg-white rounded-lg shadow-md border border-blue-100">
      <H3 className="border-b pb-2 text-blue-700">
        {item ? "Editar Pago de Gastos" : "Nuevo Pago de Gastos"}
      </H3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Proveedor */}
        <div className="md:col-span-2">
          <Label className="my-0 mt-2">Proveedor</Label>
          <Select
            isSearchable
            isClearable
            options={(providers || []).map((p) => ({
              value: p.name,
              label: `${p.name} (${JSON.parse(p.data).codigo_tr4 || "SIN CODIGO TR4"})`,
            }))}
            value={
              values.provider
                ? {
                    value: values.provider.name,
                    label: `${values.provider.name} (${values.provider.codigo_tr4 || "SIN CODIGO TR4"})`,
                  }
                : null
            }
            onChange={handleProviderChange}
            placeholder="Seleccione un proveedor"
          />
        </div>

        {/* Concepto */}
        <div>
          <Label className="my-0 mt-2">Concepto</Label>
          <Select
            isSearchable
            isClearable
            options={CONCEPT_OPTIONS}
            value={CONCEPT_OPTIONS.find((opt) => opt.value === values.concept)}
            onChange={handleConceptChange}
            placeholder="Seleccione un concepto"
          />
        </div>

        {/* Detalles de los gastos */}
        <div className="md:col-span-3 border p-3 rounded-md bg-gray-50">
          <div className="flex justify-between items-center mb-2">
            <Label className="my-0">Detalle de los gastos</Label>
            <Button
              type="button"
              onClick={handleAddExpenseDetail}
              className="px-3 py-1 text-sm"
            >
              + Añadir Gasto
            </Button>
          </div>
          {values.expenseDetails.map((expense, index) => (
            <div key={index} className="flex gap-2 mb-2">
              <CreatableSelect
                isSearchable
                isClearable
                options={(expenseConcepts || []).map((ec) => ({
                  value: ec.name,
                  label: ec.name,
                }))}
                value={
                  expense.concept
                    ? { value: expense.concept, label: expense.concept }
                    : null
                }
                onChange={(selected) =>
                  handleExpenseDetailChange(
                    index,
                    "concept",
                    selected?.value || "",
                  )
                }
                onCreateOption={(inputValue) =>
                  handleCreateExpenseConcept(inputValue, index)
                }
                placeholder="Concepto de gasto"
                className="w-full"
              />
              <input
                type="number"
                value={expense.amount}
                onChange={(e) =>
                  handleExpenseDetailChange(index, "amount", e.target.value)
                }
                className="w-full p-2 rounded-md border border-gray-300"
                placeholder="Monto"
              />
              <select
                value={expense.currency || "Bs."}
                onChange={(e) =>
                  handleExpenseDetailChange(index, "currency", e.target.value)
                }
                className="p-2 rounded-md border border-gray-300 bg-white"
              >
                <option value="Bs.">Bs.</option>
                <option value="USD">USD</option>
              </select>
              <Button
                type="button"
                onClick={() => handleRemoveExpenseDetail(index)}
                className="bg-red-500 hover:bg-red-600 px-3 py-1 w-fit"
              >
                <i className="fas fa-trash"></i>
              </Button>
            </div>
          ))}
          {values.expenseDetails.length > 0 && (
            <div className="flex flex-col items-end mt-2 border-t border-gray-200 pt-2">
              {Object.entries(
                values.expenseDetails.reduce((acc, curr) => {
                  const currency = curr.currency || "Bs.";
                  acc[currency] =
                    (acc[currency] || 0) + (parseFloat(curr.amount) || 0);
                  return acc;
                }, {}),
              ).map(([currency, total]) => (
                <span
                  key={currency}
                  className="font-bold text-lg text-gray-800"
                >
                  Total {currency}: {numberWithDecimals(total)}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Fecha de entrega de contabilidad */}
        <div>
          <Label className="my-0 mt-2">Fecha Tentativa</Label>
          <input
            value={values.tentativeDate}
            onChange={(e) =>
              setValues((prev) => ({
                ...prev,
                tentativeDate: e.target.value,
              }))
            }
            className="w-full p-2 rounded-md border border-gray-300"
            type="date" // Changed to date type
            name="tentativeDate"
          />
        </div>

        {/* Descripción */}
        {/* <div className="md:col-span-2">
          <Label className={`my-0 mt-2`}>Descripción</Label>
          <input
            value={values.description}
            onChange={(e) =>
              setValues((prev) => ({ ...prev, description: e.target.value }))
            }
            className="w-full p-2 rounded-md border border-gray-300"
            type="text"
            name="description"
            placeholder="Descripción del pago"
          />
        </div> */}
      </div>
      {item && (
        <div className="bg-blue-50 p-2 rounded text-xs text-blue-600 italic">
          Nota: Para adjuntar archivos, utilice el ícono del clip en la lista
          principal después de guardar los datos.
        </div>
      )}
      <div className="flex justify-end mt-2">
        <Button onClick={handleSubmit} className="px-6 py-2">
          {item ? "Actualizar" : "Guardar"}
        </Button>
      </div>
    </div>
  );
};

const FormUpdateStatus = ({
  item,
  currentStatus,
  values,
  reload,
  accountingDeliveryDate: initialAccountingDeliveryDate,
}) => {
  const [paymentDate, setPaymentDate] = useState(values?.paymentDate || "");
  const [accountingDeliveryDate, setAccountingDeliveryDate] = useState(
    initialAccountingDeliveryDate || "",
  );

  async function update(newStatus, date = "") {
    const payload = {
      ...item,
      Description2: JSON.stringify({
        ...values,
        status: newStatus,
        accountingDeliveryDate:
          newStatus === "Solicitado"
            ? accountingDeliveryDate
            : values.accountingDeliveryDate, // Set only when becoming 'Solicitado'
        paymentDate: date || values.paymentDate,
      }),
    };
    await requestAuth("put", `/dataSet/procedure/subData/${item.id}`, payload);
    toast.success(`Estado actualizado a ${newStatus}`);
    reload();
  }

  return (
    <div className="p-5 bg-white rounded-lg flex flex-col gap-4 shadow-xl">
      <H3 className="text-center">Flujo de Pago</H3>
      <div className="flex justify-between items-center px-4">
        <div
          className={`flex flex-col items-center ${currentStatus === "Planificado" ? "text-blue-600" : "text-gray-400"}`}
        >
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${currentStatus === "Planificado" ? "border-blue-600 font-bold" : "border-gray-300"}`}
          >
            1
          </div>
          <span className="text-[10px]">Planificado</span>
        </div>
        <div className="h-0.5 flex-grow bg-gray-200 mx-2"></div>
        <div
          className={`flex flex-col items-center ${currentStatus === "Solicitado" ? "text-blue-600" : "text-gray-400"}`}
        >
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${currentStatus === "Solicitado" ? "border-blue-600 font-bold" : "border-gray-300"}`}
          >
            2
          </div>
          <span className="text-[10px]">Solicitado</span>
        </div>
        <div className="h-0.5 flex-grow bg-gray-200 mx-2"></div>
        <div
          className={`flex flex-col items-center ${currentStatus === "Pagado" ? "text-blue-600" : "text-gray-400"}`}
        >
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${currentStatus === "Pagado" ? "border-blue-600 font-bold" : "border-gray-300"}`}
          >
            3
          </div>
          <span className="text-[10px]">Pagado</span>
        </div>
      </div>

      <div className="mt-4 border-t pt-4">
        {currentStatus === "Planificado" && (
          <div className="flex flex-col gap-2">
            <Label className="text-blue-800 font-bold">
              Fecha de Entrega a Contabilidad:
            </Label>
            <input
              type="date"
              className="p-2 border rounded"
              value={accountingDeliveryDate}
              onChange={(e) => setAccountingDeliveryDate(e.target.value)}
            />
            <Button
              disabled={!accountingDeliveryDate}
              onClick={() => update("Solicitado")}
              className="w-full bg-amber-500 hover:bg-amber-600"
            >
              Marcar como SOLICITADO
            </Button>
          </div>
        )}
        {currentStatus === "Solicitado" && (
          <div className="flex flex-col gap-2">
            <Label className="text-green-800 font-bold">
              Fecha de Pago Efectuada:
            </Label>
            <input
              type="date"
              className="p-2 border rounded"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
            />
            <Button
              disabled={!paymentDate}
              onClick={() => update("Pagado", paymentDate)}
              className="w-full bg-green-600"
            >
              Confirmar PAGO REALIZADO
            </Button>
          </div>
        )}
        {currentStatus === "Pagado" && (
          <p className="text-center text-green-600 font-bold italic">
            Este pago ya ha sido procesado completamente.
          </p>
        )}
      </div>
    </div>
  );
};

export default InputSubDataPaymentsWithItems;
